import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Monitor } from 'lucide-react';
import { TYPE_SCHEMAS, FieldDef } from './fieldSchemas';

/**
 * Definición de columnas disponibles para la tabla de elementos.
 * Cada columna puede ocultarse o reordenarse desde el selector "Columnas".
 *
 * Las columnas FIJAS (expandir, ✓ revisado, acciones) NO viven aquí —
 * las renderiza la tabla siempre.
 *
 * Hay dos familias de columnas:
 *  - NATIVAS: leen propiedades de la fila (`e.nombre`, `e.tipo`, ...).
 *  - TÉCNICAS: leen de `e.datos_tecnicos[key]` (claves del esquema por tipo).
 * Las técnicas se generan automáticamente a partir de TYPE_SCHEMAS para que
 * cualquier dato registrado pueda mostrarse como columna.
 */

export type ElementoColumnDef = {
  id: string;
  label: string;
  /** Si true se muestra por defecto cuando el usuario no tiene preferencias. */
  defaultVisible: boolean;
  /** Clases CSS extra para <TableHead>/<TableCell>. */
  className?: string;
  render: (e: any) => React.ReactNode;
};

const getColorEstado = (estado?: string) => {
  switch (estado) {
    case 'activo':
      return 'bg-green-500';
    case 'mantenimiento':
      return 'bg-yellow-500';
    case 'averiado':
      return 'bg-red-500';
    case 'anomalia':
      return 'bg-orange-500';
    case 'inactivo':
      return 'bg-gray-500';
    default:
      return 'bg-blue-500';
  }
};


const fmtTecnico = (v: any) => {
  if (v === undefined || v === null || v === '') return '-';
  if (typeof v === 'boolean') return v ? 'Sí' : 'No';
  return String(v);
};

// ---------- columnas nativas ----------

const NATIVE_COLUMNS: ElementoColumnDef[] = [
  {
    id: 'numero_elemento',
    label: 'Ref.',
    defaultVisible: true,
    className: 'w-20',
    render: (e) => (
      <span className="font-mono text-xs text-muted-foreground">
        {e.numero_elemento != null ? String(e.numero_elemento).padStart(3, '0') : '—'}
      </span>
    ),
  },
  {
    id: 'nombre',
    label: 'Nombre',
    defaultVisible: true,
    render: (e) => (
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-primary/10 text-primary">
          <Monitor className="h-4 w-4" />
        </div>
        <div className="font-medium text-sm">{e.nombre}</div>
      </div>
    ),
  },
  {
    id: 'tipo',
    label: 'Tipo',
    defaultVisible: true,
    render: (e) => (
      <Badge variant="secondary" className="text-xs">
        {e.tipo || 'Sin especificar'}
      </Badge>
    ),
  },
  {
    id: 'zona',
    label: 'Ubicación',
    defaultVisible: true,
    className: 'text-sm',
    render: (e) => e.zona || '-',
  },
  {
    id: 'marca_modelo',
    label: 'Marca / Modelo',
    defaultVisible: true,
    className: 'text-sm',
    render: (e) => [e.marca, e.modelo].filter(Boolean).join(' / ') || '-',
  },
  {
    id: 'mac_serie',
    label: 'Nº serie / MAC',
    defaultVisible: false,
    className: 'text-sm',
    render: (e) => e.mac_serie || '-',
  },
  {
    id: 'anio_fabricacion',
    label: 'Año fabricación',
    defaultVisible: false,
    className: 'text-sm',
    render: (e) => e.anio_fabricacion || '-',
  },
  {
    id: 'fecha_instalacion',
    label: 'Fecha instalación',
    defaultVisible: false,
    className: 'text-sm',
    render: (e) => e.fecha_instalacion || '-',
  },
  {
    id: 'observaciones',
    label: 'Observaciones',
    defaultVisible: false,
    className: 'text-sm max-w-xs truncate',
    render: (e) => e.observaciones || '-',
  },
  {
    id: 'estado',
    label: 'Estado',
    defaultVisible: true,
    render: (e) => (
      <div className="flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full ${getColorEstado(e.estado)}`}></div>
        <span className="text-sm capitalize">{e.estado}</span>
      </div>
    ),
  },
];

// Columnas técnicas que queremos visibles por defecto (claves de datos_tecnicos).
const TECNICOS_VISIBLES_DEFECTO = new Set<string>([
  'zona_asociada',
  'particion',
  'vr_cableado',
]);

// ---------- columnas técnicas (auto desde TYPE_SCHEMAS) ----------

function buildTecnicasColumns(): ElementoColumnDef[] {
  const seen = new Map<string, FieldDef>();
  for (const schema of TYPE_SCHEMAS) {
    for (const f of schema.fields) {
      if (!seen.has(f.key)) seen.set(f.key, f);
    }
  }
  const cols: ElementoColumnDef[] = [];
  for (const [key, field] of seen) {
    cols.push({
      id: `dt:${key}`,
      label: field.label,
      defaultVisible: TECNICOS_VISIBLES_DEFECTO.has(key),
      className: 'text-sm',
      render: (e) => {
        const dt = (e?.datos_tecnicos ?? {}) as Record<string, any>;
        return fmtTecnico(dt[key]);
      },
    });
  }
  // Ordenar alfabéticamente por label para que el picker sea navegable
  cols.sort((a, b) => a.label.localeCompare(b.label, 'es'));
  return cols;
}

export const ELEMENTO_COLUMNS: ElementoColumnDef[] = [
  ...NATIVE_COLUMNS,
  ...buildTecnicasColumns(),
];

export const ELEMENTO_COLUMNS_BY_ID: Record<string, ElementoColumnDef> = Object.fromEntries(
  ELEMENTO_COLUMNS.map((c) => [c.id, c]),
);
